import { memo, useState } from 'react';
import {
  approvalMode,
  previewLinks,
  RISK_LEVEL_WORDS,
  type CiRun,
  type Task,
  type TaskRisk,
} from '@shared/domain.ts';
import { headChecks, primaryAction } from '@/data/selectors.ts';
import { ago, cn, plural } from '@/lib/format.ts';
import { api } from '@/lib/api.ts';
import { CiPulseChip, PrChip } from './Pulses.tsx';
import { Lightbox } from './Lightbox.tsx';
import { RowAction, RowLink } from './TaskRow.tsx';
import { Shot } from './Shot.tsx';
import { Tip } from './ui.tsx';
import { KindPill } from './TaskFacts.tsx';

/**
 * One pull request waiting on approval, with everything needed to decide it
 * without leaving the list: what it fixes and how risky the routine measured
 * it to be, what it looks like, what CI said, and the button.
 *
 * The button is the board's own — `RowAction`, calling the same
 * `POST /tasks/:id/approve` every other Approve in the app calls — so there is
 * no second way to approve that could drift from the first.
 */
export const ApprovalRow = memo(function ApprovalRow({
  task,
  now,
  isAdmin,
  selected,
  ciRuns,
  projectLabel,
  onSelect,
  onApprove,
  onSignOff,
  onRetry,
}: {
  task: Task;
  now: number;
  isAdmin: boolean;
  selected: boolean;
  ciRuns: Record<string, CiRun>;
  projectLabel: string | null;
  onSelect: (id: string | null) => void;
  onApprove: (id: string) => void;
  onSignOff: (id: string) => void;
  onRetry: (id: string) => void;
}) {
  const [shotAt, setShotAt] = useState<number | null>(null);
  const primary = primaryAction(task, isAdmin);
  const previews = previewLinks(task);
  const checks = headChecks(task, ciRuns);
  // Latest first, as everywhere else a routine's screenshots are shown.
  const shots = [...task.images].reverse();

  return (
    <div
      role="button"
      tabIndex={0}
      aria-current={selected}
      onClick={() => onSelect(selected ? null : task.id)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect(selected ? null : task.id);
        }
      }}
      className={cn(
        'relative flex cursor-pointer flex-col gap-2 border-b border-cardline px-3 py-3 outline-none sm:px-[18px] sm:py-3.5',
        selected ? 'bg-cardsel shadow-[inset_3px_0_0_var(--color-cardbar)]' : 'hover:bg-cardhover',
        'focus-visible:shadow-[inset_3px_0_0_var(--color-accent)]',
      )}
    >
      {/* title, links, and the one action */}
      <div className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1.5">
        <span className="font-mono text-[11.5px] text-carddim tabular-nums">#{task.ref}</span>
        <KindPill kind={task.kind} />
        <span className="min-w-[160px] flex-1 truncate text-[14.5px] font-semibold text-cardhi">
          {task.title}
        </span>
        <span className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-1.5">
          <PrChip task={task} />
          {previews.map((link) => (
            <RowLink key={link.url} href={link.url} label={link.label} />
          ))}
          {primary ? (
            <>
              <span className="h-4 w-px bg-cardline" aria-hidden />
              <RowAction
                primary={primary}
                early={approvalMode(task) === 'auto_merge' && !task.merge_held_by}
                onApprove={() => onApprove(task.id)}
                onSignOff={() => onSignOff(task.id)}
                onRetry={() => onRetry(task.id)}
              />
            </>
          ) : null}
        </span>
      </div>

      {task.risk ? <RiskBlock risk={task.risk} /> : null}
      {task.quote ? <QuoteSnippet quote={task.quote} /> : null}

      {/* checks, and how long it has been waiting */}
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-carddim">
        <CiPulseChip task={task} now={now} />
        {checks.map((check) => (
          <CheckPill key={check.name} name={check.name} tone={check.tone} url={check.url} />
        ))}
        {projectLabel ? (
          <>
            <span aria-hidden>·</span>
            <span className="font-medium text-cardmid">{projectLabel}</span>
          </>
        ) : null}
        <span aria-hidden>·</span>
        <Tip content={`Raised ${new Date(task.created_at).toLocaleString()}`}>
          <span>opened {ago(task.created_at, now)}</span>
        </Tip>
      </div>

      {shots.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {shots.slice(0, 6).map((image, index) => (
            <Tip key={image.url} content={image.label ?? 'Screenshot'}>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  setShotAt(index);
                }}
                aria-label={`Open ${image.label ?? 'screenshot'} full size`}
                className="rg-focus block cursor-zoom-in"
              >
                <Shot url={image.url} label={image.label} className="h-16 w-[100px]" />
              </button>
            </Tip>
          ))}
          {shots.length > 6 ? (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                setShotAt(6);
              }}
              className="rg-focus cursor-zoom-in self-center pl-1 text-[12px] text-carddim hover:text-cardhi"
            >
              +{shots.length - 6} more
            </button>
          ) : null}
        </div>
      ) : null}

      {shotAt !== null ? (
        <div onClick={(event) => event.stopPropagation()}>
          <Lightbox
            images={shots}
            index={shotAt}
            onIndex={setShotAt}
            onClose={() => setShotAt(null)}
            onComment={(body) => api.comment(task.id, body)}
            projectId={task.project_id}
          />
        </div>
      ) : null}
    </div>
  );
});

const QUOTE_PREVIEW = 200;

/**
 * What the requester said, under the gists: the approval is the moment their
 * own words matter most — does this fix what they actually asked for? The
 * first 200 characters, the rest a click away.
 */
function QuoteSnippet({ quote }: { quote: string }) {
  const [open, setOpen] = useState(false);
  const long = quote.length > QUOTE_PREVIEW;
  return (
    <blockquote className="min-w-0 border-l-2 border-cardline pl-2.5 text-[12.5px] leading-relaxed whitespace-pre-wrap text-cardbody">
      <span className="font-semibold text-cardmid">They said: </span>
      {open || !long ? quote : `${quote.slice(0, QUOTE_PREVIEW).trimEnd()}…`}
      {long ? (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            setOpen((value) => !value);
          }}
          className="rg-focus ml-1.5 cursor-pointer text-[12px] font-semibold text-carddim hover:text-cardhi"
        >
          {open ? 'less' : 'more'}
        </button>
      ) : null}
    </blockquote>
  );
}

const LEVEL_TONE: Record<TaskRisk['level'], string> = {
  1: 'bg-good/13 text-good',
  2: 'bg-amber/14 text-amber',
  3: 'bg-warn/12 text-warn',
};

/**
 * The routine's own measurement, as reported. Rendered only when there is
 * one — an absent block is shown as nothing, never as a guessed level.
 */
export function RiskBlock({ risk }: { risk: TaskRisk }) {
  const gist = [risk.issue_gist, risk.fix_gist].filter(Boolean);
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px] text-cardbody">
      <span
        className={cn('shrink-0 rounded-full px-2.5 py-[3px] text-[11.5px] font-semibold', LEVEL_TONE[risk.level])}
      >
        Risk {risk.level} · {RISK_LEVEL_WORDS[risk.level]}
      </span>
      <span className="text-cardmid">
        UX {risk.ux_impact} · {plural(risk.lines_changed, 'line')} changed
      </span>
      {/* Its own line: the gist is the part read to decide, so it gets the
          row's full width rather than whatever the pills leave over. */}
      {risk.issue_ref !== null || gist.length > 0 ? (
        <span className="line-clamp-2 w-full min-w-0">
          {risk.issue_ref !== null ? <span className="font-mono text-[11.5px] text-carddim">#{risk.issue_ref} </span> : null}
          {gist.join(' → ')}
        </span>
      ) : null}
    </div>
  );
}

const CHECK_GLYPH = { passing: '✓', failing: '✕', running: '◐', none: '·' } as const;
const CHECK_TONE = {
  passing: 'text-good',
  failing: 'text-warn',
  running: 'text-amber',
  none: 'text-carddim',
} as const;

function CheckPill({
  name,
  tone,
  url,
}: {
  name: string;
  tone: keyof typeof CHECK_GLYPH;
  url: string | null;
}) {
  const body = (
    <span className="inline-flex items-center gap-1 rounded-full border border-cardline px-2 py-[1px] whitespace-nowrap">
      <span className={cn('text-[10.5px]', CHECK_TONE[tone])} aria-hidden>
        {CHECK_GLYPH[tone]}
      </span>
      <span className="text-cardmid">{name}</span>
    </span>
  );
  if (!url) return body;
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      onClick={(event) => event.stopPropagation()}
      className="rg-focus no-underline hover:no-underline hover:brightness-95"
    >
      {body}
    </a>
  );
}
